import dynamic from 'next/dynamic';
import { ProfileProps } from '@common/types';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import {
  useProfileContext,
  ACTIONS,
} from '@components/Contexts/ProfileContext';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import { useBi } from '@components/Hooks/useBi';
import { useCopyToClipboard } from '@components/Hooks/useCopyToClipboard';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import { partnersProfilePagePageLoaded } from '@wix/bi-logger-marketplace/v2';
import { useErrorMonitor } from '@wix/fe-essentials-standalone';
import _ from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { animateScroll } from 'react-scroll';
import ProfileDesktop from './ProfileDesktop';
import ProfileMobile from './ProfileMobile';
import { PARTNER_PROFILE, PROFILE_PAGE_SECTIONS } from '@utils/constants';
import useProfileContactButton from './hooks/useProfileContactButton';

export const AgencyProfileContactFlow = dynamic(
  () => import('@components/AgencyProfileContactFlow'),
  {
    ssr: false,
  },
);

export const CompactBrief = dynamic(() => import('@components/CompactBrief'), {
  ssr: false,
});

const Profile: FC = () => {
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const { userDetails } = useHeaderContext();
  const {
    partnerProfile,
    isRenderedInModal,
    host,
    entry,
    origin,
    appStatus,
    enabled,
    isMobileUserAgent,
    dispatch,
    accountCertifications,
  } = useProfileContext();
  const {
    state: { numberOfResults: numberOfTemplates },
  } = useTemplatesContext();
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const { shouldUseAgencyContactForm, handleLoggedOutUser, isClientLoggedIn } =
    useProfileContactButton();
  const errorMonitor = useErrorMonitor();
  const [isMPBriefOpen, setIsMPBriefOpen] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [briefTimedOutMessage, setBriefTimedOutMessage] = useState(false);

  const { logProfilePageClick, isInMarketplace } = useProfilePageBi();
  const navigationRefs = useRef<any>({});

  const setCopyStatus = useCallback(
    (status: null | 'success' | 'error') => {
      dispatch({ type: ACTIONS.SET_COPY_STATUS, copyStatus: status });
    },
    [dispatch],
  );

  const { copy: copyFn } = useCopyToClipboard({
    onSuccess: () => setCopyStatus('success'),
    onError: () => setCopyStatus('error'),
    onClear: () => setCopyStatus(null),
  });

  const copy = useCallback(
    async (text: string): Promise<void> => {
      return new Promise<void>((resolve) => {
        copyFn(text);
        resolve();
      });
    },
    [copyFn],
  );

  const profileUrl = `${host}/${PARTNER_PROFILE}/${partnerProfile.slug || ''}`;
  const contactStatus: 'success' | 'error' | null = showSuccessNotification
    ? 'success'
    : showErrorNotification
    ? 'error'
    : null;

  const clearStatusMap: Record<'success' | 'error', () => void> = useMemo(
    () => ({
      success: () => setShowSuccessNotification(false),
      error: () => setShowErrorNotification(false),
    }),
    [setShowSuccessNotification, setShowErrorNotification],
  );

  const handleHashChange = useCallback(() => {
    if (_.includes(window.location.hash, '#brief')) {
      const shouldOpenContactModal = enabled || shouldUseAgencyContactForm;
      setIsMPBriefOpen(shouldOpenContactModal);
    } else {
      setIsMPBriefOpen(false);
    }
  }, [userDetails, enabled]);

  useEffect(() => {
    window.addEventListener('popstate', handleHashChange);
    const shouldOpenContactModal = enabled || shouldUseAgencyContactForm;
    if (_.includes(window.location.hash, '#brief') && shouldOpenContactModal) {
      handleHashChange();
    }
    return () => {
      window.removeEventListener('popstate', handleHashChange);
    };
  }, [handleHashChange]);

  function closeModal() {
    history.replaceState(history.state, '', '#');
    setIsMPBriefOpen(false);
    setBriefTimedOutMessage(false);
  }

  useEffect(() => {
    // Prevent the browser from restoring a stale scroll position on
    // reload — we handle scroll-to-hash ourselves.
    history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    try {
      // Disable hash-based scrolling for mobile view since we handle tab scrolling manually
      if (location.hash && !isMobileView) {
        const sectionId = location.hash.slice(1);
        if (document && document.getElementById(sectionId)) {
          const offset =
            document.getElementById(sectionId)!.getBoundingClientRect().top -
            238;
          animateScroll.scrollMore(offset, {
            duration: 1500,
            delay: 100,
            smooth: true,
          });
        }
      }
    } catch (err) {
      errorMonitor.captureException(err as Error);
    }
  }, [errorMonitor, isMobileView]);

  const [showBack, setShowBack] = useState(false);
  useEffect(() => {
    setShowBack(document.referrer.indexOf('hire') > -1);
  }, []);

  const onCopyClicked = () => {
    logProfilePageClick('share');
  };

  function onContactClickedMarketplace() {
    logProfilePageClick('connect');
    history.replaceState(history.state, '', '#brief');
    handleHashChange();
  }

  const onContactClickedAgency = () => {
    logProfilePageClick('connect');
    if (isClientLoggedIn) {
      history.replaceState(history.state, '', '#brief');
      handleHashChange();
    } else {
      handleLoggedOutUser();
    }
  };

  function headerOnClick() {
    logProfilePageClick('marketplace top button');
  }

  function scrollToSection(
    section: (typeof PROFILE_PAGE_SECTIONS)[keyof typeof PROFILE_PAGE_SECTIONS],
    isMiniContactPanelVisible?: boolean,
  ) {
    if (navigationRefs.current) {
      const offset = isMobileView
        ? isMiniContactPanelVisible
          ? 215
          : 420
        : 168;
      const element = navigationRefs.current[section] as
        | HTMLElement
        | undefined;
      if (element) {
        const targetPosition =
          element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: targetPosition - offset,
          behavior: 'smooth',
        });

        if (section === PROFILE_PAGE_SECTIONS.REVIEWS) {
          // for accessibility
          const titleById = document.getElementById(
            'reviews-title',
          ) as HTMLElement | null;
          if (titleById) {
            titleById.focus({ preventScroll: true });
          }
        }
      }
    }
  }

  useEffect(() => {
    if (appStatus === 'initialized' && !isFirstLoad) {
      logger.report(
        partnersProfilePagePageLoaded({
          sessionId,
          origin: origin || document.referrer,
          entry,
          exploreVersion: 'V2',
          provider_id: partnerProfile.id!,
          inMarketplace: isInMarketplace,
        }),
      );
      setIsFirstLoad(true);
    }
  }, [appStatus, entry, isFirstLoad, origin, partnerProfile.id, sessionId]);

  const profileProps: ProfileProps = {
    onContactClicked: shouldUseAgencyContactForm
      ? onContactClickedAgency
      : onContactClickedMarketplace,
    onCopyClicked,
    headerOnClick,
    showBack,
    certifications: accountCertifications || [],
    scrollToSection,
    sessionId: sessionId || '',
    partnerProfile,
    isRenderedInModal: isRenderedInModal || false,
    numberOfTemplates,
    navigationRefs,
    copy,
    profileUrl,
    contactStatus,
    clearStatusMap,
  };

  return (
    <>
      {isMobileView ? (
        <ProfileMobile {...profileProps} />
      ) : (
        <ProfileDesktop {...profileProps} />
      )}
      {shouldUseAgencyContactForm ? (
        <AgencyProfileContactFlow
          isMobileView={isMobileView}
          isOpen={isMPBriefOpen}
          onClose={closeModal}
          onSuccess={() => setShowSuccessNotification(true)}
          onError={() => setShowErrorNotification(true)}
        />
      ) : (
        <CompactBrief
          isMobileView={isMobileView}
          closeModal={closeModal}
          isOpen={isMPBriefOpen}
          setShowSuccessNotification={setShowSuccessNotification}
          setShowErrorNotification={setShowErrorNotification}
          setBriefTimedOutMessage={setBriefTimedOutMessage}
          briefTimedOutMessage={briefTimedOutMessage}
        />
      )}
    </>
  );
};

export default Profile;
